/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Alert, Button, Heading, HFlow, Icon, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { CheckboxField, resolveName } from 'components/form'
import { MChatPerguntaEnum } from 'graphql/types.generated'
import { useEffect, useState } from 'react'
import { useField } from 'react-final-form'
import { MetaRoot } from 'util/metaPath'
import { PecTag } from 'view/atendimentos/list/PecTag'

import { MChatRfModalButtonClose } from '../../components/MChatRfModalButtonClose'
import { MCHAT_PERGUNTAS } from '../../mchat/model-mchat'
import { getMChatPerguntaNumber } from '../../mchat/utils-mchat'
import { ConsultaSeguimentoFieldModel, ConsultaSeguimentoSelectionMode } from '../model-consultaSeguimento'
import { getPerguntasSelecionadasFieldHasError } from '../utils-consultaSeguimento'

interface ConsultaSeguimentoModalTabSelecaoProps {
  path: MetaRoot<ConsultaSeguimentoFieldModel>
  perguntasFalhasNoMChat: MChatPerguntaEnum[]
  startFailed: boolean
  onClose: () => void
  clearStartFailed: () => void
}

export function ConsultaSeguimentoModalTabSelecao(props: Readonly<ConsultaSeguimentoModalTabSelecaoProps>) {
  const { path, perguntasFalhasNoMChat, startFailed, onClose, clearStartFailed } = props

  const theme = useTheme()
  const styles = createStyles(theme)

  const { meta } = useField(resolveName(path.perguntasSelecionadas), {
    subscription: { error: true, touched: true, dirty: true },
  })

  const {
    input: { value: selectionMode, onChange: setSelectionMode },
  } = useField<ConsultaSeguimentoSelectionMode>(resolveName(path.selectionMode), { subscription: { value: true } })

  const hasPerguntasFalhasNoMChat = perguntasFalhasNoMChat?.length > 0
  const isMChatRiscoBaixo = hasPerguntasFalhasNoMChat && perguntasFalhasNoMChat?.length < 3

  const hasError = getPerguntasSelecionadasFieldHasError(meta, selectionMode, isMChatRiscoBaixo, startFailed)

  const [showAlertError, setShowAlertError] = useState(isMChatRiscoBaixo || startFailed)

  const toggleMode = () => {
    setSelectionMode(
      selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
        ? ConsultaSeguimentoSelectionMode.MANUAL
        : ConsultaSeguimentoSelectionMode.AUTOMATICO
    )
  }

  const closeAlertError = () => {
    setShowAlertError(false)
    clearStartFailed()
  }

  useEffect(() => {
    if (startFailed) {
      setShowAlertError(true)
    }
  }, [startFailed])

  useEffect(() => {
    if (!hasError) {
      setShowAlertError(false)
    }
  }, [hasError])

  useEffect(() => {
    if (startFailed && !hasError) {
      clearStartFailed()
    }
  }, [clearStartFailed, hasError, startFailed])

  return (
    <VFlow vSpacing={2}>
      <HFlow alignItems='flex-start' justifyContent='flex-end'>
        <MChatRfModalButtonClose onClose={onClose} />
      </HFlow>
      <Heading level={1}>Entrevista de seguimento</Heading>
      <VFlow>
        <Text>
          <strong>
            Na Entrevista de Seguimento é necessário reaplicar as perguntas nas quais a criança inicialmente falhou no
            M-Chat-R.
          </strong>{' '}
          Cada página da entrevista corresponde a uma pergunta do M-CHAT-R.{' '}
        </Text>
        {!hasPerguntasFalhasNoMChat && (
          <Alert type='warning' inline styles={{ wrapper: styles.alert }}>
            A entrevista de seguimento é recomendada para resultados dentro do Risco Médio no M-CHAT-R.
          </Alert>
        )}
        {showAlertError && (
          <Alert type='danger' inline styles={{ wrapper: styles.alert }} onCloseClick={closeAlertError}>
            É necessário selecionar pelo menos 3 perguntas para iniciar a Entrevista de Seguimento.
          </Alert>
        )}
        <Box style={styles.box(hasError)}>
          <VFlow vSpacing={0}>
            <HFlow alignItems='center' justifyContent='space-between' style={styles.header}>
              <HFlow alignItems='center' hSpacing={0.5}>
                {hasPerguntasFalhasNoMChat && !isMChatRiscoBaixo ? (
                  <Text fontWeight='bold'>Perguntas do M-CHAT-R nas quais a criança falhou</Text>
                ) : (
                  <Text fontWeight='bold'>Selecione no mínimo 3 perguntas do M-CHAT-R em que a criança falhou</Text>
                )}
                {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO && (
                  <Tooltip text='As perguntas do M-CHAT-R nas quais a criança falhou foram selecionadas automaticamente.'>
                    <PecTag icon='sparkles' style={styles.tag}>
                      Automático
                    </PecTag>
                  </Tooltip>
                )}
              </HFlow>
              {hasPerguntasFalhasNoMChat && !isMChatRiscoBaixo && (
                <Button
                  kind={selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO ? 'primary' : 'normal'}
                  skin='outline'
                  size='small'
                  onClick={toggleMode}
                >
                  <Icon
                    icon={selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO ? 'penOutline' : 'redo'}
                    style={styles.buttonIcon}
                  />
                  {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO
                    ? 'Editar seleção'
                    : 'Restaurar seleção automática'}
                </Button>
              )}
            </HFlow>
            {selectionMode === ConsultaSeguimentoSelectionMode.AUTOMATICO && (
              <VFlow style={styles.list}>
                {perguntasFalhasNoMChat.map((pergunta) => (
                  <Text key={pergunta}>
                    {getMChatPerguntaNumber(pergunta)}. “{MCHAT_PERGUNTAS[pergunta]}”
                  </Text>
                ))}
              </VFlow>
            )}
            {selectionMode === ConsultaSeguimentoSelectionMode.MANUAL && (
              <VFlow style={styles.list}>
                {Object.keys(MCHAT_PERGUNTAS).map((pergunta: MChatPerguntaEnum) => (
                  <HFlow key={pergunta} alignItems='center' justifyContent='space-between' hSpacing={2}>
                    <Text>
                      {getMChatPerguntaNumber(pergunta)}. “{MCHAT_PERGUNTAS[pergunta]}”
                      {perguntasFalhasNoMChat.includes(pergunta) && (
                        <span css={styles.tagFalhou}>
                          <span css={styles.tagFalhouIconWrapper}>
                            <Icon size={1} icon='infoCircleOutline' />
                          </span>
                          <Text color='inherit'>Falhou no M-Chat-R</Text>
                        </span>
                      )}
                    </Text>
                    <CheckboxField name={path.perguntasSelecionadas} value={pergunta} />
                  </HFlow>
                ))}
              </VFlow>
            )}
          </VFlow>
        </Box>
      </VFlow>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  alert: css`
    width: 100%;
    padding: 0 0.5rem;
  `,
  tag: css`
    color: ${theme.pallete.primary.main};
    background: ${theme.pallete.primary.c90};
  `,
  buttonIcon: css`
    margin-right: 0.25rem;
  `,
  box: (hasError: boolean) => css`
    padding: 0;
    border-color: ${hasError ? theme.pallete.status.danger.main : theme.pallete.divider};
  `,
  header: css`
    padding: 0.5rem 1rem;
    border-bottom: 1px solid ${theme.pallete.divider};
  `,
  list: css`
    & > div {
      border-bottom: 1px solid ${theme.pallete.divider};
      margin: 0;
      padding: 0.25rem 1rem;
      &:last-child {
        border-bottom: 0;
      }
    }
  `,
  tagFalhou: css`
    color: ${theme.pallete.primary.main};
    margin-left: 0.25rem;
  `,
  tagFalhouIconWrapper: css`
    margin-right: 0.25rem;
    svg {
      margin-bottom: -0.25rem;
    }
  `,
})
